import { ErrorMessage } from '@hookform/error-message';
import { Control, Message, MultipleFieldErrors, useController, useFormContext, useFormState } from 'react-hook-form';
import { FormGroup, Input, InputGroup, InputGroupText, Label } from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import classNames from 'classnames';
import styles from './form-input.module.css';

const FormInput = ({
  label,
  type = 'text',
  options = [],
  name,
  control,
  valueAsNumber = false,
  Icon = null,
  inline = false,
  placeholder = '',
  disabled = false,
  valueAsBoolean = false,
  required = false,
  rules = {},
  handleChange = () => {},
  allowDecimal = true,
  invalid = false,
  includeEmptyOption = true,
  min,
  max,
  errorMessageRenderer = (data: { message: Message; messages?: MultipleFieldErrors }) => (
    <small className="text-danger py-2">{data.message}</small>
  ),
}: {
  name: string;
  control: Control<any>;
  label?: string;
  type?: 'text' | 'number' | 'checkbox' | 'select' | 'datetime-local';
  options?: any[];
  valueAsNumber?: boolean;
  Icon?: any;
  inline?: boolean;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: string;
  valueAsBoolean?: boolean;
  required?: boolean;
  rules?: any;
  handleChange?: (value: any) => void;
  allowDecimal?: boolean;
  invalid?: boolean;
  includeEmptyOption?: boolean;
  min?: number;
  errorMessageRenderer?: (data: { message: Message; messages?: MultipleFieldErrors }) => React.ReactNode;
  max?: number;
}) => {
  const { field } = useController({
    control,
    name,
    rules,
  });

  const { errors } = useFormState({ control });

  // Helper function to get nested field errors
  const getNestedFieldError = (errors: any, fieldPath: string) => {
    return fieldPath.split('.').reduce((obj, key) => {
      return obj?.[key];
    }, errors);
  };

  const fieldError = getNestedFieldError(errors, name);

  const renderInput = () => {
    const handleInputOnChange = (e: any) => {
      const value = e.target.value;

      if (handleChange) {
        handleChange(value);
      }

      if (type === 'number' || valueAsNumber) {
        if (value === '') {
          field.onChange(0);
          return;
        }

        const regex = allowDecimal
          ? /^-?(\d+\.?\d*|\.\d+)$/ // Allows decimals
          : /^-?\d+$/; // Integers only

        if (!regex.test(value)) {
          return;
        }

        let cleanNumber;

        if (allowDecimal) {
          const num = parseFloat(value);

          if (value.endsWith('.')) {
            cleanNumber = num.toString() + '.';
          } else {
            cleanNumber = num.toString();
          }
        } else {
          cleanNumber = parseInt(value, 10).toString();
        }

        field.onChange(parseFloat(cleanNumber));
        return;
      }

      if (type === 'checkbox') {
        field.onChange(e.target.checked);
        return;
      }

      if (valueAsBoolean) {
        field.onChange(Boolean(value));
        return;
      }

      field.onChange(value);
    };

    const handleInputOnBlur = (e: any) => {
      const value = e.target.value;

      if (value.endsWith('.')) {
        field.onChange(value.split('.')[0]);
      }
    };

    const handleNumberInputKeyDown = (e: any) => {
      if (e.key === '.' && !allowDecimal) {
        e.preventDefault();
      }
    };
    const handleInputOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    const inputEle = () => {
      const errorClass = fieldError ? 'border-red-500 is-invalid' : '';
      switch (type) {
        case 'select':
          return (
            <select
              className={`form-select ${errorClass}`}
              {...field}
              onChange={handleInputOnChange}
              disabled={disabled}
            >
              {includeEmptyOption && <option value="">-</option>}
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        case 'checkbox':
          return (
            <Input
              {...field}
              type="checkbox"
              className="m-0"
              checked={field.value}
              onChange={handleInputOnChange}
              disabled={disabled}
              required={required}
              invalid={invalid}
            />
          );
        case 'number':
          return (
            <Input
              {...field}
              className={`m-0 text-left ${errorClass}`}
              type="number"
              value={Number(field.value).toString()}
              placeholder={placeholder}
              onChange={handleInputOnChange}
              onFocus={handleInputOnFocus}
              disabled={disabled}
              required={required}
              onBlur={handleInputOnBlur}
              onKeyDown={handleNumberInputKeyDown}
              invalid={invalid}
              min={min}
              max={max}
            />
          );
        case 'datetime-local':
          return (
            <Flatpickr
              {...field}
              className={`form-control m-0 text-left ${errorClass} ${disabled ? 'disabled' : ''}`}
              disabled={disabled}
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                allowInput: true,
              }}
            />
          );
        default:
          return (
            <Input
              {...field}
              className={`m-0 text-left ${errorClass}`}
              type={type}
              placeholder={placeholder}
              onChange={handleInputOnChange}
              onFocus={handleInputOnFocus}
              disabled={disabled}
              required={required}
              onBlur={handleInputOnBlur}
              invalid={invalid}
            />
          );
      }
    };

    return (
      <InputGroup className={inline ? 'd-flex align-items-center justify-content-between flex-row' : ''}>
        {Icon && (
          <InputGroupText style={{ width: '48px', height: '44x' }}>
            <Icon style={{ width: '16px', height: '16px' }} />
          </InputGroupText>
        )}
        {inputEle()}
      </InputGroup>
    );
  };

  return (
    <FormGroup
      className={classNames(styles.formInputContainer, {
        [styles.inputFormInlineContainer]: inline,
      })}
    >
      {label && (
        <Label
          for={name}
          className={classNames('w-auto', {
            'mb-2': !inline,
            'mb-0': inline,
          })}
        >
          {label}
        </Label>
      )}
      <div className="w-auto">{renderInput()}</div>
      {fieldError && <ErrorMessage errors={errors} name={name} render={errorMessageRenderer} />}
    </FormGroup>
  );
};

export default FormInput;
